import type {
  EmailDraftResponse,
  EmailScheduleItem,
  EmailScheduleResponse,
  EmailSendAgainResponse,
  EmailSentResponse,
  EmailTemplatesResponse,
} from '@league/shared/contract';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import { buttonClass } from '@league/ui';

import { Button, Field, Input } from '../../../components/ui';
import { ApiError, apiFetch, apiPost, apiPut } from '../../../lib/api';
import { leagueDate, leagueTime } from '../../../lib/when';
import { EmailPreviewFrame } from '../EmailPieces';
import type { LeagueContext } from '../LeagueWorkspace';

const keyOf = (item: EmailScheduleItem) => `${item.kind}:${item.sessionId ?? 'league'}`;

/**
 * THE LEAGUE'S EMAILS, AS A SCHEDULE (Courtney, 2026-10-12). Every email the league will send and
 * when; Edit (its subject, words and who it goes to), Send now, and Cancel. No history: an email is
 * upcoming, waiting, sent, cancelled or off.
 */
export function EmailsTab() {
  const { league } = useOutletContext<LeagueContext>();
  const queryClient = useQueryClient();
  const [open, setOpen] = useState<string | null>(null);
  const [again, setAgain] = useState<string | null>(null);
  const [sentAgain, setSentAgain] = useState<{ key: string; count: number } | null>(null);
  const [confirming, setConfirming] = useState<string | null>(null);
  /* Each open editor's unsaved edits, by email. See `act` below. */
  const unsaved = useRef(new Map<string, Record<string, unknown>>());

  const schedule = useQuery({
    refetchInterval: 15000,
    queryKey: ['email-schedule', league.id],
    queryFn: () => apiFetch<EmailScheduleResponse>(`/api/leagues/${league.id}/email-schedule`),
  });
  const refresh = () => void queryClient.invalidateQueries({ queryKey: ['email-schedule', league.id] });
  const selectedForSend = schedule.data?.emails.find(item => `${item.kind}:${item.sessionId ?? 'league'}` === confirming);
  const sendingDraft = useQuery({
    queryKey: ['email-draft', league.id, selectedForSend?.kind, selectedForSend?.sessionId],
    enabled: Boolean(selectedForSend),
    queryFn: () => {
      const params = new URLSearchParams({ kind: selectedForSend!.kind, ...(selectedForSend!.sessionId ? {sessionId: selectedForSend!.sessionId} : {}) });
      return apiFetch<EmailDraftResponse>(`/api/leagues/${league.id}/email-schedule/draft?${params}`);
    },
  });

  /*
    SEND IT NOW SENDS WHAT IS ON THE SCREEN (2026-10-20). It sent the last SAVED email, so wording she
    had changed and not saved never reached anybody, and nothing said so. Her edits are saved first,
    then the email goes, in the one press.
  */
  const act = useMutation({
    mutationFn: async ({ item, action }: { item: EmailScheduleItem; action: 'send' | 'cancel' | 'undo' }) => {
      if (action !== 'send') {
        return apiPut(`/api/leagues/${league.id}/email-schedule/draft`, { kind: item.kind, sessionId: item.sessionId, cancelled: action === 'cancel' });
      }
      const edits = unsaved.current.get(keyOf(item));
      if (edits) await apiPut(`/api/leagues/${league.id}/email-schedule/draft`, { kind: item.kind, sessionId: item.sessionId, ...edits });
      return apiPost(`/api/leagues/${league.id}/email-schedule/send`, { kind: item.kind, sessionId: item.sessionId });
    },
    onSuccess: () => {
      setConfirming(null);
      refresh();
      void queryClient.invalidateQueries({ queryKey: ['email-draft', league.id] });
    },
  });

  if (schedule.isPending) return <p className="text-sm text-ink-3">Loading…</p>;
  if (schedule.isError) return <p role="alert">Could not load this league’s emails. Try again.</p>;

  const when = (item: EmailScheduleItem): string => {
    switch (item.state) {
      case 'scheduled':
        return item.dueAt
          ? `${leagueDate(item.dueAt, league.timeZone, { weekday: 'short', month: 'short', day: 'numeric' })}, ${leagueTime(item.dueAt, league.timeZone)}`
          : '';
      case 'waiting':
        return `After session ${item.sessionNumber}’s scores are closed`;
      case 'sent':
        return item.sentAt ? `Sent ${leagueDate(item.sentAt, league.timeZone)}, ${leagueTime(item.sentAt, league.timeZone)}` : 'Sent';
      case 'queued': return 'Queued for delivery';
      case 'failed': return 'Some messages could not be delivered';
      case 'manual': return 'Automatic sending is disabled';
      case 'cancelled':
        return 'Cancelled';
      case 'off':
        return 'Off in Settings';
      case 'missed':
        return 'Not sent';
      case 'ongoing':
        return 'When a player registers or is invited';
    }
  };

  const quiet = `${buttonClass('plain')} whitespace-nowrap hover:border-stroke disabled:cursor-wait`;

  return (
    <div className="flex flex-col gap-8">
      <div className="overflow-clip rounded-[14px] border border-light bg-paper">
        <ul className="flex flex-col">
          {schedule.data.emails.map((item) => {
            const key = keyOf(item);
            return (
              <li key={key} className="border-b border-light last:border-b-0">
                <div className="flex min-h-[60px] flex-wrap items-center justify-between gap-x-4 gap-y-1 px-4 py-2">
                  <div className="min-w-0">
                    <p className="text-[16px] font-semibold">
                      {item.title}
                      {item.sessionNumber && item.kind !== 'kickoff' ? <span className="font-normal text-ink-2"> · Session {item.sessionNumber}</span> : null}
                    </p>
                    <p className="tabular text-[13px] text-ink-2">
                      {when(item)}
                      {item.edited ? ' · Edited' : ''}
                      {sentAgain?.key === key ? ` · Sent again to ${sentAgain.count} ${sentAgain.count === 1 ? 'player' : 'players'}` : ''}
                    </p>
                  </div>
                  <div className="flex flex-wrap items-center gap-1">
                    {item.canSendAgain ? (
                      <button type="button" className={quiet} aria-expanded={again === key} onClick={() => setAgain(again === key ? null : key)}>
                        {again === key ? 'Close' : 'Send again'}
                      </button>
                    ) : null}
                    {item.canEdit ? (
                      <button type="button" className={quiet} aria-expanded={open === key} onClick={() => setOpen(open === key ? null : key)}>
                        {open === key ? 'Close' : 'Preview / Edit'}
                      </button>
                    ) : null}
                    {item.canSendNow ? (
                      confirming === key ? (
                        <>
                          <Button size="inline" variant="secondary" className="shrink-0 whitespace-nowrap" disabled={act.isPending || !sendingDraft.isSuccess || !sendingDraft.data.recipients?.some(r => r.included)} onClick={() => act.mutate({ item, action: 'send' })}>
                            {act.isPending ? 'Sending…' : 'Send it now'}
                          </Button>
                          <button type="button" className={quiet} onClick={() => setConfirming(null)}>
                            Not yet
                          </button>
                        </>
                      ) : (
                        <Button size="inline" variant="secondary" onClick={() => { setOpen(key); setConfirming(key); }}>
                          {item.state === 'failed' ? 'Retry failed messages' : 'Send now'}
                        </Button>
                      )
                    ) : null}
                    {item.canCancel ? (
                      <button type="button" className={`${quiet} hover:border-rust/35 hover:text-rust`} disabled={act.isPending} onClick={() => act.mutate({ item, action: 'cancel' })}>
                        Cancel email
                      </button>
                    ) : null}
                    {item.state === 'cancelled' ? (
                      <button type="button" className={quiet} disabled={act.isPending} onClick={() => act.mutate({ item, action: 'undo' })}>
                        Undo
                      </button>
                    ) : null}
                  </div>
                </div>
                {open === key ? (
                  <EmailEditor
                    leagueId={league.id}
                    item={item}
                    onSaved={refresh}
                    onUnsaved={(edits) => {
                      if (edits) unsaved.current.set(key, edits);
                      else unsaved.current.delete(key);
                    }}
                  />
                ) : null}
                {again === key ? (
                  <SendAgain
                    leagueId={league.id}
                    zone={league.timeZone}
                    item={item}
                    onSent={(count) => {
                      setAgain(null);
                      setSentAgain({ key, count });
                    }}
                  />
                ) : null}
              </li>
            );
          })}
        </ul>
      </div>
      {act.isError ? (
        <p role="alert" className="text-[13px] text-signal-d">
          {act.error instanceof ApiError ? act.error.message : 'That did not work. Try again.'}
        </p>
      ) : null}

      <SurveyLink leagueId={league.id} />
    </div>
  );
}

/** One email, as the director edits it: subject, words, who gets it, and the email as sent. */
function EmailEditor({ leagueId, item, onSaved, onUnsaved }: { leagueId: string; item: EmailScheduleItem; onSaved: () => void; onUnsaved: (edits: Record<string, unknown> | null) => void }) {
  const params = new URLSearchParams({ kind: item.kind, ...(item.sessionId ? { sessionId: item.sessionId } : {}) });
  const draft = useQuery({
    queryKey: ['email-draft', leagueId, item.kind, item.sessionId],
    queryFn: () => apiFetch<EmailDraftResponse>(`/api/leagues/${leagueId}/email-schedule/draft?${params.toString()}`),
  });
  /*
    THE FORM STARTS AGAIN ONLY AFTER ITS OWN SAVE (2026-10-19), so "Use the standard email" shows the
    standard words. It was keyed on the fetch time, and this query key is shared with the Send
    button's, so any refetch (opening Send, returning to the tab) wiped what she was typing.
  */
  const [revision, setRevision] = useState(0);
  if (draft.isPending) return <p className="px-4 pb-4 text-sm text-ink-3">Loading…</p>;
  if (draft.isError) return <p role="alert" className="px-4 pb-4 text-sm">Could not load this email.</p>;
  return (
    <EmailForm
      key={revision}
      leagueId={leagueId}
      item={item}
      draft={draft.data}
      onUnsaved={onUnsaved}
      onSaved={() => {
        void draft.refetch().then(() => setRevision((r) => r + 1));
        onSaved();
      }}
    />
  );
}

function EmailForm({ leagueId, item, draft, onSaved, onUnsaved }: { leagueId: string; item: EmailScheduleItem; draft: EmailDraftResponse; onSaved: () => void; onUnsaved: (edits: Record<string, unknown> | null) => void }) {
  const [subject, setSubject] = useState(draft.subject);
  const [body, setBody] = useState(draft.body);
  const [include, setInclude] = useState(() => new Set((draft.recipients ?? []).filter((r) => r.included).map((r) => r.playerId)));

  /*
    Tells the tab what Save would save, or null when nothing has changed, so Send it now can save it.
    Through a ref: the tab hands a new function every render, and nothing here should re-run for that.
  */
  const report = useRef(onUnsaved);
  report.current = onUnsaved;
  useEffect(() => {
    const savedInclude = (draft.recipients ?? []).filter((r) => r.included).map((r) => r.playerId);
    const includeChanged = Boolean(draft.recipients) && (savedInclude.length !== include.size || savedInclude.some((playerId) => !include.has(playerId)));
    const changed = subject !== draft.subject || body !== draft.body || includeChanged;
    report.current(changed ? { subject, body, ...(draft.recipients ? { include: [...include] } : {}) } : null);
  }, [subject, body, include, draft]);
  useEffect(() => () => report.current(null), []);
  const [preview, setPreview] = useState(false);
  const id = `${item.kind}-${item.sessionId ?? 'league'}`;

  const save = useMutation({
    mutationFn: (change: Record<string, unknown>) =>
      apiPut(`/api/leagues/${leagueId}/email-schedule/draft`, { kind: item.kind, sessionId: item.sessionId, ...change }),
    onSuccess: onSaved,
  });

  const notes = { unsubscribed: 'Unsubscribed', away: 'Away that session', not_invited: 'Not yet invited to this league', no_email: 'No email address', bounced: 'Email could not be delivered' } as const;

  return (
    <div className="flex flex-col gap-4 border-t border-light bg-chalk px-4 py-4">
      {draft.sender ? <div className="text-sm text-ink-2 break-words"><p>From: {draft.sender.name} &lt;{draft.sender.address}&gt;</p><p>Reply to: {draft.sender.replyTo}</p></div> : null}
      <Field label="Subject" htmlFor={`${id}-subject`}>
        <Input id={`${id}-subject`} value={subject} onChange={(e) => setSubject(e.target.value)} />
      </Field>
      <Field label="Email" htmlFor={`${id}-body`}>
        <textarea
          id={`${id}-body`}
          value={body}
          onChange={(e) => setBody(e.target.value)}
          rows={16}
          className="w-full rounded border border-control-border bg-paper px-3.5 py-3 text-[15px] leading-relaxed focus-visible:border-deep"
        />
      </Field>

      {draft.recipients ? (
        <fieldset className="border-0 p-0">
          <legend className="text-[13px] font-semibold">
            Goes to {include.size} {include.size === 1 ? 'player' : 'players'}
          </legend>
          <ul className="mt-2 grid gap-x-6 sm:grid-cols-2">
            {draft.recipients.map((r) => (
              <li key={r.playerId}>
                <label className="flex min-h-11 items-center gap-3 text-[15px]">
                  <input
                    type="checkbox"
                    className="size-5 accent-deep"
                    checked={include.has(r.playerId)}
                    disabled={r.note === 'unsubscribed' || r.note === 'no_email' || r.note === 'bounced'}
                    onChange={(e) =>
                      setInclude((current) => {
                        const next = new Set(current);
                        if (e.target.checked) next.add(r.playerId);
                        else next.delete(r.playerId);
                        return next;
                      })
                    }
                  />
                  <span className="min-w-0">
                    {r.name}
                    {r.note ? <span className="text-[13px] text-ink-3"> · {notes[r.note]}</span> : null}
                  </span>
                </label>
              </li>
            ))}
          </ul>
        </fieldset>
      ) : null}

      <div className="flex flex-wrap items-center gap-3">
        <Button
          size="app"
          disabled={save.isPending}
          onClick={() => save.mutate({ subject, body, ...(draft.recipients ? { include: [...include] } : {}) })}
        >
          {save.isPending ? 'Saving…' : 'Save'}
        </Button>
        {draft.edited ? (
          <Button size="app" variant="secondary" disabled={save.isPending} onClick={() => save.mutate({ restore: true })}>
            Use the standard email
          </Button>
        ) : null}
        <Button size="app" variant="secondary" onClick={() => setPreview(!preview)}>
          {preview ? 'Hide preview' : 'Preview'}
        </Button>
        {save.isError ? (
          <span role="alert" className="text-[13px] text-signal-d">
            {save.error instanceof ApiError ? save.error.message : 'Could not save that.'}
          </span>
        ) : null}
      </div>

      {preview ? <EmailPreviewFrame html={draft.html} title={`${item.title} — as players receive it`} /> : null}
    </div>
  );
}

/**
 * SEND AGAIN (owner, 2026-10-16): the email as it went, to the players she ticks. Nobody starts
 * ticked — the usual case is the one player who says she never got it, and a whole league emailed
 * twice by one press is the mistake to make impossible.
 */
function SendAgain({ leagueId, zone, item, onSent }: { leagueId: string; zone: string; item: EmailScheduleItem; onSent: (count: number) => void }) {
  const ref = { kind: item.kind, ...(item.sessionId ? { sessionId: item.sessionId } : {}) };
  const sent = useQuery({
    queryKey: ['email-sent', leagueId, item.kind, item.sessionId],
    queryFn: () => apiFetch<EmailSentResponse>(`/api/leagues/${leagueId}/email-schedule/sent?${new URLSearchParams(ref).toString()}`),
  });
  const [chosen, setChosen] = useState<Set<string>>(new Set());
  const send = useMutation({
    mutationFn: () => apiPost<EmailSendAgainResponse>(`/api/leagues/${leagueId}/email-schedule/send-again`, { ...ref, playerIds: [...chosen] }),
    onSuccess: (result) => onSent(result.queued),
  });

  if (sent.isPending) return <p className="px-4 pb-4 text-sm text-ink-3">Loading…</p>;
  if (sent.isError) return <p role="alert" className="px-4 pb-4 text-sm">Could not load this email.</p>;

  const notes = { unsubscribed: 'Unsubscribed', bounced: 'Email bounced', no_email: 'No email', away: 'Away that session' } as const;
  const id = `again-${item.kind}-${item.sessionId ?? 'league'}`;

  return (
    <div className="flex flex-col gap-4 border-t border-light bg-chalk px-4 py-4">
      <fieldset className="border-0 p-0">
        <legend className="sr-only">Send again to</legend>
        <ul className="grid gap-x-6 sm:grid-cols-2">
          {sent.data.recipients.map((r) => (
            <li key={r.playerId}>
              <label className="flex min-h-11 items-center gap-3 text-[15px]">
                <input
                  type="checkbox"
                  id={`${id}-${r.playerId}`}
                  className="size-5 accent-deep"
                  checked={chosen.has(r.playerId)}
                  disabled={r.note !== null}
                  onChange={(e) =>
                    setChosen((current) => {
                      const next = new Set(current);
                      if (e.target.checked) next.add(r.playerId);
                      else next.delete(r.playerId);
                      return next;
                    })
                  }
                />
                <span className="min-w-0">
                  {r.name}
                  {r.note ? (
                    <span className="text-[13px] text-ink-3"> · {notes[r.note]}</span>
                  ) : r.sentAt ? (
                    <span className="tabular text-[13px] text-ink-3"> · Sent {leagueDate(r.sentAt, zone)}</span>
                  ) : null}
                </span>
              </label>
            </li>
          ))}
        </ul>
      </fieldset>
      <div className="flex flex-wrap items-center gap-3">
        <Button size="app" disabled={chosen.size === 0 || send.isPending} onClick={() => send.mutate()}>
          {send.isPending ? 'Sending…' : `Send to ${chosen.size} ${chosen.size === 1 ? 'player' : 'players'}`}
        </Button>
        {send.isError ? (
          <span role="alert" className="text-[13px] text-signal-d">
            {send.error instanceof ApiError ? send.error.message : 'Could not send that.'}
          </span>
        ) : null}
      </div>
    </div>
  );
}

/**
 * THE SURVEY LINK, which stays the director's to pass on — its answers are anonymous, and a link the
 * platform sent would be a platform that knows who was asked.
 */
function SurveyLink({ leagueId }: { leagueId: string }) {
  const [copied, setCopied] = useState(false);
  const templates = useQuery({
    queryKey: ['email-templates', leagueId, null],
    queryFn: () => apiFetch<EmailTemplatesResponse>(`/api/leagues/${leagueId}/email-templates`),
  });
  const survey = templates.data?.templates.find((t) => t.kind === 'survey');
  if (!survey) return null;
  return (
    <div className="flex flex-wrap items-center justify-between gap-3 rounded-[14px] border border-light bg-paper px-4 py-3">
      <div>
        <p className="text-[16px] font-semibold">{survey.title}</p>
        <p className="text-[13px] text-ink-2">{survey.when}</p>
      </div>
      {/* A row action, like the schedule's rows above it. */}
      <Button
        variant="plain"
        onClick={() => {
          void navigator.clipboard.writeText(`Subject: ${survey.subject}\n\n${survey.body}`).then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
          });
        }}
      >
        {copied ? 'Copied' : 'Copy'}
      </Button>
    </div>
  );
}
